import React, { useState, useEffect } from 'react';
import { tenantApi } from '../lib/tenant-api';
import Modal from './Modal';
import FileDropzone from './FileDropzone';

interface DocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
}

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  result?: any;
  error?: string;
}

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileUploadStatus[]>([]);
  const [generateEmbeddings, setGenerateEmbeddings] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedFiles([]);
      setGenerateEmbeddings(true);
      setError(null);
    }
  }, [isOpen]);

  const handleFilesSelected = (files: File[]) => {
    if (files.length > 0) {
      const validFiles: FileUploadStatus[] = [];
      let hasError = false;

      for (const file of files) {
        // Validate file type
        if (!file.name.endsWith('.md') && !file.name.endsWith('.markdown')) {
          setError(`Invalid file: ${file.name}. Please select only Markdown files (.md or .markdown).`);
          hasError = true;
          continue;
        }

        validFiles.push({
          file,
          status: 'pending'
        });
      }

      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
        if (!hasError) {
          setError(null);
        }
      }
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file first.');
      return;
    }

    setIsUploading(true);
    setError(null);
    let hasSuccessfulImport = false;

    for (let i = 0; i < selectedFiles.length; i++) {
      const fileStatus = selectedFiles[i];

      // Update status to uploading
      setSelectedFiles(prev => prev.map((f, idx) =>
        idx === i ? { ...f, status: 'uploading' as const } : f
      ));

      try {
        const response = await tenantApi.uploadDocuments(fileStatus.file, generateEmbeddings);

        // Update status to success
        setSelectedFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'success' as const, result: response } : f
        ));

        if (response.results?.successful_imports > 0) {
          hasSuccessfulImport = true;
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || err.message || 'Upload failed';

        // Update status to error
        setSelectedFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'error' as const, error: errorMessage } : f
        ));
      }
    }

    setIsUploading(false);

    if (hasSuccessfulImport) {
      onUploadComplete();
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setGenerateEmbeddings(true);
    setIsUploading(false);
    setError(null);
    onClose();
  };

  const allCompleted = selectedFiles.length > 0 && selectedFiles.every(f => f.status === 'success' || f.status === 'error');
  const successCount = selectedFiles.filter(f => f.status === 'success').length;
  const errorCount = selectedFiles.filter(f => f.status === 'error').length;
  const totalImports = selectedFiles.reduce((sum, f) => sum + (f.result?.results?.successful_imports || 0), 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload Documents">
      <div className="space-y-6">
        {/* File Upload Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Document Markdown File
          </label>

          <div className="relative">
            <FileDropzone
              onFilesSelected={handleFilesSelected}
              acceptedTypes={['text/markdown', '.md', '.markdown']}
              maxFiles={undefined}
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
              {selectedFiles.map((fileStatus, index) => (
                <div
                  key={index}
                  className={`p-3  border ${fileStatus.status === 'success' ? 'bg-green-50 border-green-200' :
                    fileStatus.status === 'error' ? 'bg-red-50 border-red-200' :
                      fileStatus.status === 'uploading' ? 'bg-indigo-50 border-indigo-200' :
                        'bg-gray-50 border-gray-200'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {fileStatus.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(fileStatus.file.size / 1024).toFixed(2)} KB
                      </p>
                      {fileStatus.status === 'success' && fileStatus.result && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ {fileStatus.result.results?.successful_imports || 0} documents imported
                        </p>
                      )}
                      {fileStatus.status === 'error' && (
                        <p className="text-xs text-red-600 mt-1">
                          ✗ {fileStatus.error}
                        </p>
                      )}
                      {fileStatus.status === 'uploading' && (
                        <p className="text-xs text-indigo-600 mt-1">
                          Uploading...
                        </p>
                      )}
                    </div>
                    {fileStatus.status === 'pending' && !isUploading && (
                      <button
                        onClick={() => removeFile(index)}
                        className="ml-2 text-gray-400 hover:text-red-500"
                      >
                        ✕
                      </button>
                    )}
                    {fileStatus.status === 'uploading' && (
                      <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                    )}
                    {fileStatus.status === 'success' && (
                      <div className="ml-2 text-green-500">✓</div>
                    )}
                    {fileStatus.status === 'error' && (
                      <div className="ml-2 text-red-500">✗</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 ">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Upload Summary */}
        {allCompleted && (
          <div className="p-4 bg-gray-50 border border-gray-200 ">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Upload Summary</h3>

            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{selectedFiles.length}</div>
                <div className="text-sm text-gray-500">Files Processed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{successCount}</div>
                <div className="text-sm text-gray-500">Successful</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{errorCount}</div>
                <div className="text-sm text-gray-500">Failed</div>
              </div>
            </div>

            {totalImports > 0 && (
              <div className="mt-3 p-3 bg-green-50 ">
                <p className="text-sm text-green-800 text-center">
                  <strong>{totalImports}</strong> total documents imported
                </p>
              </div>
            )}
          </div>
        )}

        {/* Embedding Options */}
        <div className="pt-6 border-t border-gray-200">
          <div className="flex items-center">
            <input
              id="generate-embeddings"
              name="generate-embeddings"
              type="checkbox"
              className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 "
              checked={generateEmbeddings}
              onChange={(e) => setGenerateEmbeddings(e.target.checked)}
            />
            <label htmlFor="generate-embeddings" className="ml-3 text-sm font-medium text-gray-700">
              Generate AI embeddings for uploaded content
            </label>
          </div>
          <p className="mt-1 text-xs text-gray-500 ml-7">
            This enables AI-powered search and recommendations for the uploaded content.
          </p>
        </div>

        {/* Markdown Format Help */}
        <div className="p-4 bg-indigo-50 border border-indigo-200 ">
          <h4 className="text-sm font-medium text-indigo-800 mb-2">Expected Markdown Format</h4>
          <pre className="text-xs text-indigo-700 bg-indigo-100 p-2  whitespace-pre-wrap break-words">
            {`name: Privacy Policy
content: This policy explains how we collect, use, and protect your personal information when you use our services. We are committed to maintaining the highest standards of privacy protection.

name: FAQ - How to reset password
content: To reset your password, click on the 'Forgot Password' link on the login page. Enter your email address and follow the instructions sent to your email.

name: User Guide - Getting Started
content: Welcome to our platform! This guide will help you get started with the basic features and functionality of our system.`}
          </pre>
          <p className="text-xs text-indigo-600 mt-2">
            <strong>Note:</strong> Each entry must have both 'name:' and 'content:' fields. Separate multiple entries with blank lines.
          </p>
        </div>
      </div>

      {/* Modal Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {allCompleted ? 'Close' : 'Cancel'}
        </button>

        {!allCompleted && (
          <button
            type="button"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || isUploading}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {generateEmbeddings ? 'Uploading & Generating Embeddings...' : 'Uploading...'}
              </>
            ) : (
              `Upload ${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}${generateEmbeddings ? ' + Embeddings' : ''}`
            )}
          </button>
        )}
      </div>
    </Modal>
  );
};
